import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import useApiHook from "../hooks/useApiHook";
import { useAuth } from "./auth-context";

const NotificationContext = createContext({
    activeNotification: null,
    unreadCount: 0,
    inboxNotifications: [],
    closeNotification: () => { },
    pushNotification: () => { },
    deleteInboxNotification: async () => { },
    clearInboxNotifications: async () => { },
    loadInboxNotifications: async () => [],
    refreshUnreadCount: async () => 0,
    notifyTranscriptionCompleted: () => { },
});

const NOTIFICATION_COUNT_POLL_MS = 15000;
const BROWSER_NOTIFICATION_ICON = "/logo.png";

export const NotificationProvider = ({ children }) => {
    const [notificationQueue, setNotificationQueue] = useState([]);
    const [activeNotification, setActiveNotification] = useState(null);
    const [unreadCount, setUnreadCount] = useState(0);
    const [inboxNotifications, setInboxNotifications] = useState([]);
    const shownNotificationKeysRef = useRef(new Set());
    const defaultTitleRef = useRef(typeof document !== 'undefined' ? document.title : 'Sbobbi');
    const { isAuthenticated, isLoading, user } = useAuth();
    const { makeRequest } = useApiHook();

    useEffect(() => {
        if (activeNotification || notificationQueue.length === 0) {
            return;
        }

        const [nextNotification, ...remainingQueue] = notificationQueue;
        setActiveNotification(nextNotification);
        setNotificationQueue(remainingQueue);
    }, [activeNotification, notificationQueue]);

    const enqueueNotification = useCallback((notification) => {
        if (!activeNotification) {
            setActiveNotification(notification);
            return;
        }

        setNotificationQueue((currentQueue) => [...currentQueue, notification]);
    }, [activeNotification]);

    const closeNotification = useCallback(() => {
        setActiveNotification(null);
    }, []);

    const pushNotification = useCallback((notification) => {
        if (!notification) {
            return;
        }

        enqueueNotification({
            severity: notification.severity || 'success',
            title: notification.title || '',
            message: notification.message || '',
            actionLabel: notification.actionLabel || null,
            actionTo: notification.actionTo || null
        });
    }, [enqueueNotification]);

    const refreshUnreadCount = useCallback(async () => {
        if (!isAuthenticated) {
            setUnreadCount(0);
            return 0;
        }

        const result = await makeRequest('/user/notifications/count');
        const count = result?.state === 'success' ? result.data.count || 0 : 0;
        setUnreadCount(count);
        return count;
    }, [isAuthenticated, makeRequest]);

    const loadInboxNotifications = useCallback(async () => {
        if (!isAuthenticated) {
            setInboxNotifications([]);
            setUnreadCount(0);
            return [];
        }

        const result = await makeRequest('/user/notifications');
        const notifications = result?.state === 'success' && Array.isArray(result.data) ? result.data : [];
        setInboxNotifications(notifications);
        return notifications;
    }, [isAuthenticated, makeRequest]);

    const deleteInboxNotification = useCallback(async (notificationId) => {
        if (!notificationId) {
            return;
        }

        await makeRequest('/user/notifications/delete', 'post', { notificationId });
        setInboxNotifications((currentNotifications) => currentNotifications.filter((notification) => notification.id !== notificationId));
        await refreshUnreadCount();
    }, [makeRequest, refreshUnreadCount]);

    const clearInboxNotifications = useCallback(async () => {
        await makeRequest('/user/notifications/clear', 'post');
        setInboxNotifications([]);
        await refreshUnreadCount();
    }, [makeRequest, refreshUnreadCount]);

    useEffect(() => {
        if (isLoading) {
            return;
        }

        if (!isAuthenticated) {
            setUnreadCount(0);
            setInboxNotifications([]);
            shownNotificationKeysRef.current.clear();
            return;
        }

        refreshUnreadCount();

        const intervalId = setInterval(() => {
            refreshUnreadCount();
        }, NOTIFICATION_COUNT_POLL_MS);

        return () => clearInterval(intervalId);
    }, [isAuthenticated, isLoading, refreshUnreadCount]);

    useEffect(() => {
        if (
            typeof window === 'undefined' ||
            !('Notification' in window) ||
            !isAuthenticated ||
            Notification.permission !== 'default'
        ) {
            return;
        }

        Notification.requestPermission().catch(() => {
            // Ignore permission request failures; DB fallback still covers the event.
        });
    }, [isAuthenticated]);

    useEffect(() => {
        if (typeof document === 'undefined') {
            return;
        }

        document.title = unreadCount > 0
            ? `(${unreadCount}) ${defaultTitleRef.current}`
            : defaultTitleRef.current;
    }, [unreadCount]);

    const isLiveNotificationVisible = useCallback(() => {
        if (typeof document === 'undefined') {
            return true;
        }

        return document.visibilityState === 'visible' && document.hasFocus();
    }, []);

    const acknowledgeDisplayedNotification = useCallback((fileId) => {
        makeRequest('/user/notifications/live-ack', 'post', { fileId });
    }, [makeRequest]);

    const showBrowserNotification = useCallback(async ({ fileId, fileName, title, message, notificationKey }) => {
        if (typeof window === 'undefined' || !('Notification' in window)) {
            return false;
        }

        let permission = Notification.permission;

        if (permission === 'default') {
            try {
                permission = await Notification.requestPermission();
            } catch {
                permission = 'denied';
            }
        }

        if (permission !== 'granted') {
            return false;
        }

        const notification = new Notification(title, {
            body: message,
            icon: BROWSER_NOTIFICATION_ICON,
            tag: notificationKey
        });

        notification.onclick = () => {
            window.focus();
            if (fileId) {
                window.location.href = `/files/${fileId}`;
            }
            notification.close();
        };

        await makeRequest('/user/notifications/background-store', 'post', {
            fileId,
            fileName
        });
        await refreshUnreadCount();
        return true;
    }, [makeRequest, refreshUnreadCount]);

    const notifyTranscriptionCompleted = useCallback(({ fileId, fileName }) => {
        if (!fileId) {
            return;
        }

        if (user?.transcriptionNotificationSettings?.inApp === false) {
            return;
        }

        const notificationKey = `transcription-completed:${fileId}`;
        if (shownNotificationKeysRef.current.has(notificationKey)) {
            return;
        }

        const notificationPayload = {
            key: notificationKey,
            type: 'transcription-completed',
            title: 'Trascrizione completata',
            message: fileName ? `Il file ${fileName} e pronto.` : 'Il file e pronto.',
            fileId
        };

        if (!isLiveNotificationVisible()) {
            showBrowserNotification({
                fileId,
                fileName,
                title: notificationPayload.title,
                message: notificationPayload.message,
                notificationKey
            }).then((wasShown) => {
                if (wasShown) {
                    shownNotificationKeysRef.current.add(notificationKey);
                }
            });
            return;
        }

        shownNotificationKeysRef.current.add(notificationKey);
        acknowledgeDisplayedNotification(fileId);
        enqueueNotification(notificationPayload);
    }, [acknowledgeDisplayedNotification, enqueueNotification, isLiveNotificationVisible, showBrowserNotification, user]);

    const value = useMemo(() => ({
        activeNotification,
        unreadCount,
        inboxNotifications,
        closeNotification,
        pushNotification,
        deleteInboxNotification,
        clearInboxNotifications,
        loadInboxNotifications,
        refreshUnreadCount,
        notifyTranscriptionCompleted,
    }), [
        activeNotification,
        unreadCount,
        inboxNotifications,
        closeNotification,
        pushNotification,
        deleteInboxNotification,
        clearInboxNotifications,
        loadInboxNotifications,
        refreshUnreadCount,
        notifyTranscriptionCompleted
    ]);

    return (
        <NotificationContext.Provider value={value}>
            {children}
        </NotificationContext.Provider>
    );
};

export const useNotification = () => useContext(NotificationContext);
